"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCurrentUserTradesSubject = exports.useCurrentUserTrades = void 0;
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const trade_utils_lib_1 = require("trade_utils_lib");
const SimpleFuturesAPI_1 = require("../../../apis/SimpleFuturesAPI");
const useListenOrderStatusChange_1 = require("../SimpleFuturesOrderProcessor/useListenOrderStatusChange");
/**
 * 当前用户在指定时间范围内的交易数据，用户实时交易数据也会有变化（内置监听订单下单和关单的监听）
 * @param getRequestParams
 *        获取请求参数（可以是函数、对象字面量，都尽量使用 useCallback 或者 useMemo、useState 等包装）；
 *        getRequestParams 一变化就会出发重新获取数据；
 *        但是有些情况下 getRequestParams 没有变化也要重新获取数据；比如本 hooks 内监听到订单状态改变然后触发刷新数据，导致使用了老的 getRequestParams。这种情况需要外部传递 getRequestParams 函数让内部实时获取新的值。
 * @param isActive
 *        是否激活态，比如用户关闭显示买卖点时候，或者比如页面 unfocused 的时候。
 * @returns SimpleFutures.HistoriesRespItem[] | undefined
 */
const useCurrentUserTrades = (getRequestParams, isActive) => {
    const [data, setData] = (0, react_1.useState)();
    const requestSubscription = (0, react_1.useRef)();
    const updateData = (0, react_1.useCallback)((latest) => {
        if (isActive) {
            const requestParams = typeof getRequestParams === "function" ? getRequestParams() : getRequestParams;
            if (!requestParams) {
                setData(undefined);
                return;
            }
            if (latest) {
                requestParams.end_time = Date.now();
            }
            return SimpleFuturesAPI_1.SimpleFuturesAPI.getHistories(requestParams).subscribe({
                next: (res) => {
                    setData({
                        value: res,
                        base: requestParams.base,
                        quote: requestParams.quote,
                    });
                },
                error: () => { },
            });
        }
        else {
            setData(undefined);
        }
    }, [getRequestParams, isActive]);
    // 未激活状态，就清空数据
    (0, react_1.useEffect)(() => {
        if (!isActive) {
            setData(undefined);
        }
    }, [isActive]);
    // 初始化：获取数据
    (0, react_1.useEffect)(() => {
        const subscription = updateData();
        return () => {
            subscription === null || subscription === void 0 ? void 0 : subscription.unsubscribe();
        };
    }, [updateData]);
    // 监听买卖交易变化：重新获取数据
    const requestParams = typeof getRequestParams === "function" ? getRequestParams() : getRequestParams;
    (0, useListenOrderStatusChange_1.useListenOrderStatusChange)({
        base: requestParams === null || requestParams === void 0 ? void 0 : requestParams.base,
        quote: requestParams === null || requestParams === void 0 ? void 0 : requestParams.quote,
    }, () => {
        requestSubscription.current = updateData(true);
    }, (wssData) => {
        var _a, _b;
        const portfolio = ((_b = (_a = wssData.bu_order_data) === null || _a === void 0 ? void 0 : _a.portfolio) === null || _b === void 0 ? void 0 : _b[0]) || {};
        const key = (0, trade_utils_lib_1.md5Encrypt)(`${wssData.bu_order_id}|${wssData.status}|${portfolio.position_id}`);
        return key;
    });
    (0, react_1.useEffect)(() => {
        return () => {
            var _a;
            (_a = requestSubscription.current) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        };
    }, []);
    return data;
};
exports.useCurrentUserTrades = useCurrentUserTrades;
/**
 * 对 useCurrentUserTrades 的简单封装，用于不需要直接使用 state 的场景，而是返回可订阅的数据流。
 * 参数和 useCurrentUserTrades 一样。
 * @returns Subject<SimpleFutures.HistoriesRespItem[] | undefined>
 */
const useCurrentUserTradesSubject = (getRequestParams, isActive) => {
    // 对外返回的 Subject 对象
    const dataSubject = (0, react_1.useRef)(new rxjs_1.Subject());
    const data = (0, exports.useCurrentUserTrades)(getRequestParams, isActive);
    (0, react_1.useEffect)(() => {
        dataSubject.current.next(data === null || data === void 0 ? void 0 : data.value);
    }, [data]);
    // 返回可订阅的数据流
    return dataSubject.current;
};
exports.useCurrentUserTradesSubject = useCurrentUserTradesSubject;
